<!-- Processing State - Loading Spinner -->
@if (isProcessing()) {
<div
  class="fixed inset-0 flex items-center justify-center p-4"
  style="z-index: 99999; background: rgba(0, 0, 0, 0.85);"
>
  <app-loading-spinner
    variant="overlay"
    [phrases]="loadingPhrases"
    [showProgress]="true"
    [showDots]="true"
    [maxTimeMs]="15000"
  />
</div>
}

<!-- Error State -->
@if (processingError()) {
<div
  class="fixed inset-0 flex items-center justify-center p-4"
  style="z-index: 99999; background: rgba(0, 0, 0, 0.85);"
>
  <div class="bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-auto text-center">
    <div class="mb-6">
      <div class="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
        <i class="fas fa-exclamation-triangle text-red-500 text-2xl"></i>
      </div>
    </div>
    <h3 class="text-lg font-semibold text-gray-900 mb-2">
      {{ 'common.connectionError' | translate }}
    </h3>
    <p class="text-sm text-gray-600 mb-6">
      {{ processingError() }}
    </p>
    <button
      (click)="retryConsent()"
      class="w-full text-white rounded-lg px-4 py-3 text-sm font-semibold hover:opacity-90 transition-opacity shadow-sm"
      style="background-color: #e91d63"
    >
      {{ 'common.retry' | translate }}
    </button>
  </div>
</div>
}

<!-- Main Consent Popup - Only shown on Web -->
@if (isVisible() && !isProcessing() && !processingError()) {
<div
  class="fixed inset-0 flex items-end justify-center p-0"
  style="z-index: 99999; background: rgba(0, 0, 0, 0.80);"
>
  <div
    class="bg-white shadow-2xl w-full max-w-lg mx-0 border-t border-gray-200"
  >
    <!-- Content -->
    <div class="p-6">
      <!-- Header -->
      <div class="mb-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-2">
          {{ 'consent.privacyTitle' | translate }}
        </h3>
        <p class="text-sm text-gray-600 leading-relaxed">
          {{ 'consent.privacyMsg' | translate }}
        </p>
      </div>

      <!-- Action Buttons -->
      <div class="space-y-3">
        <!-- Primary and Secondary buttons row -->
        <div class="flex gap-3">
          <button
            (click)="handleEssentialOnly()"
            class="flex-1 bg-white text-gray-700 border border-gray-300 rounded-lg px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            {{ 'consent.essentialOnly' | translate }}
          </button>
          <button
            (click)="handleAccept()"
            class="flex-1 text-white rounded-lg px-4 py-3 text-sm font-semibold hover:opacity-90 transition-opacity shadow-sm"
            style="background-color: #e91d63"
          >
            {{ 'consent.acceptAll' | translate }}
          </button>
        </div>

        <!-- Configure link -->
        <div class="text-center">
          <button
            (click)="handleConfigure()"
            class="text-sm text-gray-500 hover:text-gray-700 transition-colors underline"
          >
            {{ 'consent.configureCookies' | translate }}
          </button>
          <span class="text-gray-400 mx-2">·</span>
          <button
            (click)="handleMoreInfo()"
            class="text-sm text-gray-500 hover:text-gray-700 transition-colors underline"
          >
            {{ 'consent.moreInfo' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
}

<!-- Cookie Configuration Modal -->
@if (showCookieConfig()) {
<div
  class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center p-4"
  style="z-index: 100000"
  (click)="closeCookieConfig()"
>
  <div
    class="bg-white rounded-lg max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col"
    (click)="$event.stopPropagation()"
  >
    <!-- Header -->
    <div class="p-6 border-b border-gray-200 flex-shrink-0">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-gray-900">
          {{ 'consent.cookieSettings' | translate }}
        </h2>
        <button
          (click)="closeCookieConfig()"
          class="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <i class="fas fa-times text-gray-400"></i>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 p-6 overflow-y-auto">
      <p class="text-sm text-gray-600 mb-6">
        {{ 'consent.noTracking' | translate }}
      </p>

      <div class="space-y-4">
        <!-- Necessary Cookies -->
        <div
          class="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
        >
          <div>
            <h4 class="font-medium text-gray-900">{{ 'consent.essentialData' | translate }}</h4>
            <p class="text-sm text-gray-500">
              {{ 'consent.essentialDataDesc' | translate }}
            </p>
          </div>
          <input
            type="checkbox"
            [checked]="cookiePreferences().necessary"
            disabled
            class="w-4 h-4 text-primary-600 opacity-50"
          />
        </div>

        <!-- Functional Cookies -->
        <div
          class="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
        >
          <div>
            <h4 class="font-medium text-gray-900">{{ 'consent.functionalPreferences' | translate }}</h4>
            <p class="text-sm text-gray-500">
              {{ 'consent.functionalPreferencesDesc' | translate }}
            </p>
          </div>
          <input
            type="checkbox"
            [checked]="cookiePreferences().functional"
            (change)="toggleCookiePreference('functional')"
            class="w-4 h-4 text-primary-600"
          />
        </div>
      </div>

      <!-- Privacy Notice -->
      <div class="mt-6 p-4 bg-green-50 rounded-lg">
        <p class="text-sm text-green-800">
          <strong>{{ 'consent.yourPrivacyMatters' | translate }}</strong> No vinculamos datos entre apps/webs,
          no usamos servicios de análisis de terceros, y nunca compartimos información
          con brokers de datos ni anunciantes.
        </p>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="p-6 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0"
    >
      <button
        (click)="closeCookieConfig()"
        class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
      >
        {{ 'common.cancel' | translate }}
      </button>
      <button
        (click)="saveCookieConfig()"
        class="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
        style="background-color: #e91d63"
      >
        {{ 'consent.saveSettings' | translate }}
      </button>
    </div>
  </div>
</div>
}

<!-- Legal Info Modal -->
@if (showLegalInfo()) {
<div
  class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center p-4"
  style="z-index: 100000"
  (click)="closeLegalInfo()"
>
  <div
    class="bg-white rounded-lg max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col"
    (click)="$event.stopPropagation()"
  >
    <!-- Header -->
    <div class="p-6 text-white flex-shrink-0" style="background-color: #e91d63">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-white">{{ 'consent.legalInfo' | translate }}</h2>
        <button
          (click)="closeLegalInfo()"
          class="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
        >
          <i class="fas fa-times text-white"></i>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 p-6 overflow-y-auto">
      <p class="text-sm text-gray-600 mb-6 leading-relaxed">
        {{ 'consent.noTracking' | translate }} {{ 'consent.legalInfoDesc' | translate }}
      </p>

      <div class="space-y-4">
        <!-- Privacy Policy -->
        <button
          (click)="openLegalDocument('privacy-policy')"
          class="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
        >
          <div
            class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mr-3"
          >
            <i class="fas fa-shield-alt text-gray-600"></i>
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium text-gray-900">
              {{ 'consent.privacyPolicy' | translate }}
            </p>
            <p class="text-xs text-gray-500">{{ 'consent.viewLegalDoc' | translate }}</p>
          </div>
          <i class="fas fa-external-link-alt text-gray-400 ml-2"></i>
        </button>

        <!-- Terms of Service -->
        <button
          (click)="openLegalDocument('terms-service')"
          class="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
        >
          <div
            class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mr-3"
          >
            <i class="fas fa-file-contract text-gray-600"></i>
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium text-gray-900">
              {{ 'consent.termsOfService' | translate }}
            </p>
            <p class="text-xs text-gray-500">{{ 'consent.viewLegalDoc' | translate }}</p>
          </div>
          <i class="fas fa-external-link-alt text-gray-400 ml-2"></i>
        </button>

        <!-- Cookie Policy -->
        <button
          (click)="openLegalDocument('cookie-policy')"
          class="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
        >
          <div
            class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mr-3"
          >
            <i class="fas fa-cookie-bite text-gray-600"></i>
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium text-gray-900">{{ 'consent.cookiePolicy' | translate }}</p>
            <p class="text-xs text-gray-500">{{ 'consent.viewLegalDoc' | translate }}</p>
          </div>
          <i class="fas fa-external-link-alt text-gray-400 ml-2"></i>
        </button>

        <!-- GDPR Rights -->
        <button
          (click)="openLegalDocument('gdpr-rights')"
          class="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
        >
          <div
            class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mr-3"
          >
            <i class="fas fa-balance-scale text-gray-600"></i>
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium text-gray-900">{{ 'consent.gdprRights' | translate }}</p>
            <p class="text-xs text-gray-500">{{ 'consent.viewLegalDoc' | translate }}</p>
          </div>
          <i class="fas fa-external-link-alt text-gray-400 ml-2"></i>
        </button>
      </div>
    </div>

    <!-- Footer -->
    <div class="p-6 border-t border-gray-200 flex justify-end flex-shrink-0">
      <button
        (click)="closeLegalInfo()"
        class="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
        style="background-color: #e91d63"
      >
        {{ 'common.close' | translate }}
      </button>
    </div>
  </div>
</div>
}
